/* eslint-disable no-underscore-dangle */
import { each, isNil, isNull } from 'lodash';
import { ROOT_GETTER_PATHS, INFRA_GETTERS } from '@core/store';
import {
  MAP_WIDGETS_NUMBER_OF_ROWS_PER_PAGE,
  DATA_LOADING_STATES,
  WIDGET_LOGICAL_NAMES,
  FEATURES,
  METRIC_NAMES,
  SORT_DIRECTION,
} from '@config/Consts';
import { getLastTimeUpdated, getMetricLabel } from '@core/utils/utils';
import { RESOURCES_METRICS_MAP, SKILL_LIST_RESOURCES } from './Consts';

const metricsResource = SKILL_LIST_RESOURCES.METRICS;
const queueHealthResource = SKILL_LIST_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH;

const _getResourceDataStatus = (state, resourceName) => state.dataStatus[resourceName];
const _getLastTimeResourceDataUpdated = (state, resourceName) => state.lastTimeDataUpdated[resourceName];
const _lastTimeResourceUpdated = (state, resourceName) => getLastTimeUpdated(_getLastTimeResourceDataUpdated(state, resourceName));
const _isResourceDataObsolete = (state, resourceName) => _getResourceDataStatus(state, resourceName) === DATA_LOADING_STATES.OBSOLETE;
const _isResourceDataDown = (state, resourceName) => _getResourceDataStatus(state, resourceName) === DATA_LOADING_STATES.ERROR;
const _isResourceOutOfDate = (state, resourceName) => _isResourceDataObsolete(state, resourceName) || _isResourceDataDown(state, resourceName);

const getMetricAttributes = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_WIDGET_METRIC_ATTRIBUTES](WIDGET_LOGICAL_NAMES.SKILLS);
const getTimeFrameFilter = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_TIMEFRAME_FILTER];
const getSkillsFilter = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_FILTERED_SKILLS_IDS];
const getFilteredGroupsIds = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GROUPS_FILTER];
const getUserManagedSkills = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USER_MANAGED_SKILLS];
const getUserManagedSkillsIds = (state, getters, rootState, rootGetters) => rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_USER_MANAGED_SKILLS_IDS];
const getSkillsMetrics = state => state.skillsMetrics || [];
const resourceDataHasBeenFetched = (state, resourceName) => !isNull(state.lastTimeDataUpdated[resourceName]);
const dataHasBeenFetched = state => !isNull(state.skillsMetrics) && (!isNull(_getLastTimeResourceDataUpdated(state, metricsResource)) || !isNull(_getLastTimeResourceDataUpdated(state, queueHealthResource)));
const isWaitingForData = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.LOADING ||
  (_getResourceDataStatus(state, queueHealthResource) === DATA_LOADING_STATES.LOADING &&
    _getResourceDataStatus(state, metricsResource) === DATA_LOADING_STATES.LOADING);
const isDataObsolete = (state) => {
  const metricsIsObsolete = _isResourceDataObsolete(state, metricsResource);
  const qhIsObsolete = _isResourceDataObsolete(state, queueHealthResource);
  const metricsIsDown = _isResourceDataDown(state, metricsResource);
  const qhIsDown = _isResourceDataDown(state, queueHealthResource);
  const dataFetched = dataHasBeenFetched(state);
  const result = metricsIsObsolete || qhIsObsolete || (dataFetched && (metricsIsDown || qhIsDown));
  return result;
};
const skillsListApiIsDown = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_ACCOUNT_DATA_STATUS] === DATA_LOADING_STATES.ERROR ||
    (_isResourceDataDown(state, metricsResource) && !dataHasBeenFetched(state));
const outOfDateMetrics = (state) => {
  const resultMetrics = [];
  each(SKILL_LIST_RESOURCES, (resource) => {
    if (_isResourceOutOfDate(state, resource)) {
      each(RESOURCES_METRICS_MAP[resource], (metricName) => {
        const metricLabel = getMetricLabel(WIDGET_LOGICAL_NAMES.SKILLS, metricName);
        if (!isNil(metricLabel)) {
          resultMetrics.push(metricLabel);
        }
      });
    }
  });
  return resultMetrics;
};
const getPagingPageNumber = state => state.paging.pageNumber;
const getPagingTotalRows = state => state.paging.totalRows;
const getPagingApiOffset = state =>
  (getPagingPageNumber(state) - 1) *
  MAP_WIDGETS_NUMBER_OF_ROWS_PER_PAGE[WIDGET_LOGICAL_NAMES.SKILLS];
const getSort = state => state.sort;
const getCurrentPageSkillIds = state => state.currentPageSkillIds;
const getGlobalFiltersUpdating = (state, getters, rootState, rootGetters) =>
  rootGetters[ROOT_GETTER_PATHS.ROOT_GETTER_GLOBAL_FILTERS_UPDATING][WIDGET_LOGICAL_NAMES.SKILLS];
// In case we have couple of APIs failures
// we want to get the earliest time
const lastTimeDataUpdated = (state) => {
  const isMetricsResourceOutOfDate = _isResourceOutOfDate(state, metricsResource);
  const isQueueHealthResourceOutOfDate = _isResourceOutOfDate(state, queueHealthResource);
  let lastTimeDataWasUpdated;
  if (isMetricsResourceOutOfDate && isQueueHealthResourceOutOfDate) {
    const metricsLastUpdateTime = _lastTimeResourceUpdated(state, metricsResource);
    const queueHealthLastUpdateTime = _lastTimeResourceUpdated(state, queueHealthResource);
    lastTimeDataWasUpdated = metricsLastUpdateTime < queueHealthLastUpdateTime ?
      metricsLastUpdateTime : queueHealthLastUpdateTime;
  } else if (isMetricsResourceOutOfDate) {
    lastTimeDataWasUpdated = _lastTimeResourceUpdated(state, metricsResource);
  } else {
    lastTimeDataWasUpdated = _lastTimeResourceUpdated(state, queueHealthResource);
  }
  return lastTimeDataWasUpdated;
};

const hasContactCenterFeature = (state, getters, rootState, rootGetters) =>
  rootGetters[INFRA_GETTERS.FEATURE_STATE](FEATURES.CONTACT_CENTER);

const getPresetValues = (state, getters) => ({
  sort: getters.getSort || {
    columnName: METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  },
});

export default {
  getFilteredGroupsIds,
  getSkillsFilter,
  getTimeFrameFilter,
  getUserManagedSkills,
  getSkillsMetrics,
  dataHasBeenFetched,
  resourceDataHasBeenFetched,
  isWaitingForData,
  isDataObsolete,
  skillsListApiIsDown,
  getMetricAttributes,
  getGlobalFiltersUpdating,
  getUserManagedSkillsIds,
  getSort,
  lastTimeDataUpdated,
  outOfDateMetrics,
  getPagingTotalRows,
  getPagingApiOffset,
  getPagingPageNumber,
  getCurrentPageSkillIds,
  hasContactCenterFeature,
  getPresetValues,
};
